import React, { useContext } from "react";
import PropTypes from "prop-types";
import { mediaProfileUrl, mediaOutletUrl, toAbsoluteUrl } from "views_shared/app/javascript/utils/urls";
import AvatarProfileImage from "../../AvatarProfileImage";
import PersonHeader from "../../profiles/PersonHeader";
import ProfileHeaderLocation from "../../profiles/ProfileHeaderLocation";
import FollowControl from "../../profiles/FollowControl";
import ShareProfileButton from "../../profiles/ShareProfileButton";
import HeaderKebabMenu from "../../profiles/HeaderKebabMenu";
import MessageButton from "../../MessageButton";
import canMessageMediaProfile from "../card/messageGating";
import { MediaProfileContext } from "../data/media_profile_context_management";

const mediaProfileBanner = RAILS_ASSET_URL("/media-profile-banner.png");

// A small inline edit pen next to the name (mirrors the PR header's pen). Renders only when the
// viewer may edit; opens the given section's modal.
function HeaderEditPen({ section, label }) {
  const { viewerContext, setOpenEditModal } = useContext(MediaProfileContext);
  if (!viewerContext?.editable) return null;
  return (
    <button
      type="button"
      aria-label={label}
      className="btn btn-primary btn-sm ms-2 d-inline-flex align-items-center justify-content-center"
      style={{ width: "2rem", height: "2rem", padding: 0 }}
      onClick={() => setOpenEditModal(section)}
    >
      <i className="fa-solid fa-pencil-alt" />
    </button>
  );
}

HeaderEditPen.propTypes = {
  section: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

// Media Profile's engine header on the shared <PersonHeader> chrome — the default banner band, an
// avatar that overlaps it, the identity column (name + featured title, with an inline Edit Header
// pen for editable viewers), and the top-right actions row. The featured title
// (`title_at_workplace`) and headline are denormalized on the media_profiles view, so the header
// needs no extra fetches. Contact details and the message action no longer live in the header —
// they render in the rail's Connect card (see MediaProfile.jsx).
function MediaPersonHeader() {
  const { mediaProfile, mediaProfileId, viewerContext, setOpenEditModal, silentReload } =
    useContext(MediaProfileContext);
  const editable = !!viewerContext?.editable;

  // <AvatarWithLabel> stays the visual renderer; <AvatarProfileImage> only adds the hover-revealed
  // edit controls around it, and only for a viewer who may edit. imgOwnerId is mediaProfileId (the
  // Id::Profile id the /user_profiles/:id/profile_image route expects, not the media_profiles view
  // row id) — silentReload is the same "this saved, refetch" signal the rest of the edit surface uses.
  const avatar = (
    <AvatarProfileImage
      profileType="media"
      avatarClassName="border border-info border-4"
      size={160}
      editable={editable}
      imgOwnerId={mediaProfileId}
      imgOwnerName={mediaProfile.name}
      imgOwnerType="user_profile"
      profileImageUrl={mediaProfile.profile_image_url}
      onChange={silentReload}
    />
  );

  const banner = (
    <div className="person-header-banner">
      <img src={mediaProfileBanner} alt="" />
    </div>
  );

  // One headline slot: the view coalesces headline onto title_at_workplace when no custom headline
  // is set, so a headline that differs from that string (trimmed) is custom and takes the slot alone.
  const hasCustomHeadline =
    !!(mediaProfile.headline || "").trim() &&
    (mediaProfile.headline || "").trim() !== (mediaProfile.title_at_workplace || "").trim();

  // Unlike PR's "{title} at {company}" (assembled from two separate columns), title_at_workplace is a
  // single denormalized string ("<role> at <outlet>"), so the outlet name is linked by locating it as a
  // substring rather than templated from parts. It's composed as a suffix, and a role can itself repeat
  // the outlet's name (e.g. "Forbes contributor at Forbes"), so this uses the LAST occurrence. A miss —
  // divergent data, or any of the three columns missing — falls back to the plain text unchanged rather
  // than risk a garbled render.
  const titleAtWorkplace = mediaProfile.title_at_workplace || "";
  const workplaceIndex = mediaProfile.workplace_name ? titleAtWorkplace.lastIndexOf(mediaProfile.workplace_name) : -1;
  const hasLinkedWorkplace = !!(
    mediaProfile.title_at_workplace &&
    mediaProfile.workplace_slug &&
    mediaProfile.workplace_name &&
    workplaceIndex !== -1
  );

  // Ruled identity stack, in order: name+pen -> headline/title -> location -> badges.
  // Media has no membership source, so the badges row stays absent; location_string is a real column
  // on the media_profiles view, so it renders when present.
  const identity = (
    <>
      <h1 className="h3 fw-bold mb-1">
        {mediaProfile.name}
        <HeaderEditPen section="HEADER" label="Edit Header" />
      </h1>
      {hasCustomHeadline && <h2 className="h5 text-body-secondary mb-2">{mediaProfile.headline}</h2>}
      {!hasCustomHeadline && mediaProfile.title_at_workplace && (
        <h2 className="h5 text-body-secondary mb-2">
          {hasLinkedWorkplace ? (
            <>
              {titleAtWorkplace.slice(0, workplaceIndex)}
              <a
                href={mediaOutletUrl({
                  slug: mediaProfile.workplace_slug,
                  migrate_id: mediaProfile.workplace_migrate_id,
                })}
              >
                {mediaProfile.workplace_name}
              </a>
              {titleAtWorkplace.slice(workplaceIndex + mediaProfile.workplace_name.length)}
            </>
          ) : (
            mediaProfile.title_at_workplace
          )}
        </h2>
      )}
      <ProfileHeaderLocation location={mediaProfile.location_string} />
    </>
  );

  const canonicalUrl = toAbsoluteUrl(mediaProfileUrl({ slug: mediaProfile.slug, migrate_id: mediaProfile.migrate_id }));

  // Viewer-split actions row: a viewer of someone else's profile gets
  // [Follow] [Share Profile]; an owner/admin gets [Share Profile] [kebab] instead — the kebab never
  // renders for a non-editor, so it never ships empty to the public. Both kebab items open the same
  // header edit modal, which already carries the visibility checkbox.
  // Never yourself; anonymous viewers have no mailbox; only media people may message media people.
  const messageButton =
    !viewerContext?.isSelf && !!viewerContext?.isAuthenticated && canMessageMediaProfile(mediaProfileId) ? (
      <MessageButton profileId={mediaProfileId} openInboxThread iconClassName="fa-regular fa-message" />
    ) : null;

  const actions = viewerContext?.editable ? (
    <>
      {messageButton}
      <ShareProfileButton url={canonicalUrl} />
      <HeaderKebabMenu
        onEditProfile={() => setOpenEditModal("HEADER")}
        onEditVisibility={() => setOpenEditModal("HEADER")}
      />
    </>
  ) : (
    <>
      {/* followableId is the Id::Profile id (mediaProfileId), not the media_profiles view row id. */}
      <FollowControl followableType="Id::Profile::MediaProfile" followableId={mediaProfileId} />
      {messageButton}
      <ShareProfileButton url={canonicalUrl} />
    </>
  );

  return (
    <PersonHeader
      avatar={avatar}
      identity={identity}
      actions={actions}
      banner={banner}
      avatarWrapperClassName="media-avatar-frame"
    />
  );
}

export default MediaPersonHeader;
